import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Calendar } from "@/shared/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

type WizardDatePickerProps = {
  /** Local calendar date as YYYY-MM-DD, or "" when unset. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  "aria-label": string;
};

function parseLocalDate(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : undefined;
}

function formatLocalDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Same Popover + Calendar pattern as the Overview date picker, sized for the wizard. */
export default function WizardDatePicker({
  value,
  onChange,
  placeholder = "Select date",
  invalid,
  "aria-label": ariaLabel,
}: WizardDatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseLocalDate(value);
  const label = selected
    ? selected.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          className="h-11 w-full justify-between gap-2 rounded-[var(--radius-sm)] border-border bg-surface px-3 text-sm font-normal tracking-normal text-foreground"
        >
          <span className={selected ? "" : "text-muted-foreground"}>{label}</span>
          <CalendarIcon className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto overflow-hidden bg-white p-0 text-neutral-950 dark:bg-popover dark:text-popover-foreground">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          disabled={{ after: new Date() }}
          endMonth={new Date()}
          onSelect={(day) => {
            if (!day) return;
            onChange(formatLocalDate(day));
            setOpen(false);
          }}
        />
        <div className="flex items-center justify-between border-t border-border px-3 py-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(""); setOpen(false); }}>
            Clear
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(formatLocalDate(new Date())); setOpen(false); }}>
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
